import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Camera, X, Circle } from 'lucide-react';
import {
  GoogleAuthProvider,
  EmailAuthProvider,
  linkWithPopup,
  linkWithCredential,
  unlink,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { useAuth } from '@/hooks/useAuth';
import { changeOwnPassword, updateProviders } from '@/lib/auth/client-auth-service';
import { Button, Card, Input, LoadingSpinner } from '@/components/ui';
import { formatBsb, formatAccountNumber } from '@/lib/utils/bsb';
import { formatTime } from '@/lib/utils';
import { canManageUsers } from '@/lib/roles';
import { Timestamp } from 'firebase/firestore';

function getInitials(name: string): string {
  return (name || '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

function UserAvatar({ user, size = 80 }: { user: { photo_url?: string | null; custom_photo_url?: string | null; name?: string }; size?: number }) {
  const src = user.custom_photo_url || user.photo_url;
  if (src) {
    return (
      <img
        src={src}
        alt={user.name || 'User avatar'}
        width={size}
        height={size}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-blue-500 flex items-center justify-center text-white font-bold"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {getInitials(user.name || '')}
    </div>
  );
}

type ContactDraft = {
  telegram_username?: string;
};

type PaymentDraft = {
  beem_id?: string;
  eft_account_name?: string;
  eft_bsb?: string;
  eft_account_number?: string;
};

type AdminDraft = {
  account_status: 'pending' | 'active' | 'disabled' | 'conflict_review';
  roles: {
    player?: boolean;
    app_admin?: boolean;
    entertainment_admin?: boolean;
  };
  password_login_enabled: boolean;
};

export function ProfilePage() {
  const { user: authUser } = useAuth();
  const { currentUser, updateProfile, adminUpdateUserProfile, saving, loading, error } = useUserProfile();

  const urlParams = new URLSearchParams(window.location.search);
  const targetUserId = urlParams.get('userId');
  const isAdminMode = targetUserId !== null && targetUserId !== authUser?.id && currentUser !== null && canManageUsers(currentUser);

  // Contact section editing
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState<ContactDraft>({});

  // Payment section editing
  const [editingPayment, setEditingPayment] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState<PaymentDraft>({});

  // Password change
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState({ current: '', next: '', confirm: '' });

  // Link password provider
  const [showLinkPassword, setShowLinkPassword] = useState(false);
  const [linkPasswordDraft, setLinkPasswordDraft] = useState({ email: '', password: '' });
  const [linkPasswordSaving, setLinkPasswordSaving] = useState(false);

  // Avatar editing
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Sync camera stream to video element whenever stream or ref changes
  useEffect(() => {
    const video = videoRef.current;
    if (cameraStream && video) {
      video.srcObject = cameraStream;
      const playResult = video.play();
      if (playResult instanceof Promise) {
        playResult.catch(console.error);
      }
    }
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  // Admin controls editing
  const [editingAdmin, setEditingAdmin] = useState(false);
  const [adminDraft, setAdminDraft] = useState<AdminDraft>({
    account_status: 'active',
    roles: { player: true, app_admin: false, entertainment_admin: false },
    password_login_enabled: false,
  });
  const [adminSaving, setAdminSaving] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        No profile data available.
      </div>
    );
  }

  const isOwnProfile = authUser?.id === currentUser.id;
  const canEditProfile = isOwnProfile || isAdminMode;
  const displayName = currentUser.name || 'Unknown Player';
  const role = canManageUsers(currentUser) ? 'admin' : currentUser.id === 'spectator' ? 'spectator' : 'player';

  const hasEft = !!(
    currentUser.eft_account_name ||
    currentUser.eft_bsb ||
    currentUser.eft_account_number
  );

  // Contact editing
  const startEditingContact = () => {
    setContactDraft({
      telegram_username: currentUser.telegram_username ?? '',
    });
    setEditingContact(true);
  };

  const cancelEditingContact = () => {
    setEditingContact(false);
    setContactDraft({});
  };

  const handleSaveContact = async () => {
    try {
      if (isAdminMode && targetUserId) {
        await adminUpdateUserProfile(targetUserId, contactDraft);
      } else {
        await updateProfile(contactDraft);
      }
      setEditingContact(false);
      setContactDraft({});
      toast.success('Contact info updated');
    } catch (err) {
      toast.error('Failed to save contact info. Please try again.');
    }
  };

  // Payment editing
  const startEditingPayment = () => {
    setPaymentDraft({
      beem_id: currentUser.beem_id ?? '',
      eft_account_name: currentUser.eft_account_name ?? '',
      eft_bsb: currentUser.eft_bsb ?? '',
      eft_account_number: currentUser.eft_account_number ?? '',
    });
    setEditingPayment(true);
  };

  const cancelEditingPayment = () => {
    setEditingPayment(false);
    setPaymentDraft({});
  };

  const handleSavePayment = async () => {
    try {
      if (isAdminMode && targetUserId) {
        await adminUpdateUserProfile(targetUserId, paymentDraft);
      } else {
        await updateProfile(paymentDraft);
      }
      setEditingPayment(false);
      setPaymentDraft({});
      toast.success('Bank details updated');
    } catch (err) {
      toast.error('Failed to save bank details. Please try again.');
    }
  };

  // Password change
  const startChangingPassword = () => {
    setPasswordDraft({ current: '', next: '', confirm: '' });
    setShowPassword(false);
    setChangingPassword(true);
  };

  const cancelChangingPassword = () => {
    setChangingPassword(false);
    setPasswordDraft({ current: '', next: '', confirm: '' });
  };

  const handlePasswordChange = async () => {
    if (!passwordDraft.current) {
      toast.error('Current password is required.');
      return;
    }
    if (passwordDraft.next.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (passwordDraft.next !== passwordDraft.confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await changeOwnPassword(passwordDraft.current, passwordDraft.next);
      setChangingPassword(false);
      setPasswordDraft({ current: '', next: '', confirm: '' });
      toast.success('Password updated successfully.');
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        toast.error('Current password is incorrect.');
      } else {
        toast.error('Failed to update password. Please try again.');
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  // Provider linking
  const canUnlink = (providerId: string) => {
    const providers = currentUser.provider_ids ?? [];
    return providers.length > 1 && providers.includes(providerId);
  };

  const handleLinkGoogle = async () => {
    const currentFirebaseUser = auth.currentUser;
    if (!currentFirebaseUser) return;
    try {
      await linkWithPopup(currentFirebaseUser, new GoogleAuthProvider());
      const nextProviders = [...new Set([...(currentUser.provider_ids ?? []), 'google.com'])];
      await updateProviders(nextProviders);
      toast.success('Google account linked');
    } catch (err) {
      console.error('Failed to link Google account:', err);
      toast.error('Failed to link Google account');
    }
  };

  const handleLinkPassword = async () => {
    const currentFirebaseUser = auth.currentUser;
    if (!currentFirebaseUser) return;
    const { email, password } = linkPasswordDraft;
    if (!email || password.length < 6) {
      toast.error('Enter a valid email and password (at least 6 characters).');
      return;
    }
    setLinkPasswordSaving(true);
    try {
      await linkWithCredential(currentFirebaseUser, EmailAuthProvider.credential(email, password));
      const nextProviders = [...new Set([...(currentUser.provider_ids ?? []), 'password'])];
      await updateProviders(nextProviders, true);
      setShowLinkPassword(false);
      setLinkPasswordDraft({ email: '', password: '' });
      toast.success('Password login enabled');
    } catch (err) {
      console.error('Failed to set password:', err);
      toast.error('Failed to set password');
    } finally {
      setLinkPasswordSaving(false);
    }
  };

  const handleUnlink = async (providerId: string) => {
    const currentFirebaseUser = auth.currentUser;
    if (!currentFirebaseUser) return;
    try {
      await unlink(currentFirebaseUser, providerId);
      const nextProviders = (currentUser.provider_ids ?? []).filter((id) => id !== providerId);
      await updateProviders(nextProviders, nextProviders.includes('password'));
      toast.success('Provider unlinked');
    } catch (err) {
      console.error('Failed to unlink provider:', err);
      toast.error('Failed to unlink provider');
    }
  };

  // Avatar editing handlers
  const MAX_AVATAR_SIZE_MB = 2;
  const VALID_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  const handleAvatarFileSelect = (file: File) => {
    if (!VALID_AVATAR_TYPES.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or GIF image.');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_AVATAR_SIZE_MB}MB.`);
      return;
    }
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAvatarInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAvatarFileSelect(file);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  // Admin controls editing
  const startEditingAdmin = () => {
    setAdminDraft({
      account_status: currentUser.account_status ?? 'active',
      roles: {
        player: currentUser.roles?.player ?? true,
        app_admin: currentUser.roles?.app_admin ?? false,
        entertainment_admin: currentUser.roles?.entertainment_admin ?? false,
      },
      password_login_enabled: currentUser.password_login_enabled ?? false,
    });
    setEditingAdmin(true);
  };

  const cancelEditingAdmin = () => {
    setEditingAdmin(false);
  };

  const handleSaveAdmin = async () => {
    if (!targetUserId) return;
    setAdminSaving(true);
    try {
      const isSelf = authUser?.id === currentUser.id;
      await adminUpdateUserProfile(targetUserId, {
        account_status: adminDraft.account_status,
        roles: {
          player: adminDraft.roles.player === true,
          app_admin: isSelf ? true : adminDraft.roles.app_admin === true,
          entertainment_admin: adminDraft.roles.entertainment_admin === true,
        },
        password_login_enabled: adminDraft.password_login_enabled,
      });
      setEditingAdmin(false);
      toast.success('Admin settings updated');
    } catch (err) {
      toast.error('Failed to update admin settings. Please try again.');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleSaveAvatar = async () => {
    if (!avatarPreview) return;
    setAvatarSaving(true);
    try {
      // Placeholder: actual Firebase Storage upload will be added later
      await updateProfile({ custom_photo_url: avatarPreview });
      setEditingAvatar(false);
      setAvatarPreview(null);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error('Failed to update profile photo. Please try again.');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleCancelAvatar = () => {
    setEditingAvatar(false);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview(null);
    stopCamera();
  };

  // Camera handlers
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Camera not supported in this browser. Please use upload instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });
      setCameraStream(stream);
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Could not access camera. Please use upload instead.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg');
      setAvatarPreview(dataUrl);
    }
    // Stop camera
    cameraStream?.getTracks().forEach(track => track.stop());
    setCameraStream(null);
  };

  const stopCamera = () => {
    cameraStream?.getTracks().forEach(track => track.stop());
    setCameraStream(null);
  };

  const infoFields = [
    { label: 'Email', value: currentUser.email || '—' },
    { label: 'Telegram Username', value: currentUser.telegram_username || '—' },
  ];

  const memberSinceFields = [
    { label: 'Member Since', value: currentUser.created_at ? formatTime(currentUser.created_at as Timestamp) : '—' },
    { label: 'Last Active', value: currentUser.last_active ? formatTime(currentUser.last_active as Timestamp) : '—' },
  ];

  const handleUpdatePaymentPreference = async (method: 'digital' | 'cash') => {
    if (!isOwnProfile) return;
    try {
      await updateProfile({ default_buyin_method: method });
      toast.success('Payment preference updated');
    } catch (err) {
      toast.error('Failed to update payment preference');
    }
  };

  const handleUpdateDefaultPaymentMethod = async (method: 'beem' | 'eft') => {
    if (!isOwnProfile) return;
    try {
      await updateProfile({ default_payment_method: method });
      toast.success('Default payment method updated');
    } catch (err) {
      toast.error('Failed to update default payment method');
    }
  };

  return (
    <div className="max-w-md mx-auto py-6 px-4 space-y-6 min-h-screen bg-[#f5f7f8]">
      {isAdminMode && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center gap-3">
          <span className="text-yellow-600">👁️</span>
          <div>
            <div className="text-sm font-semibold text-yellow-800">Admin Mode</div>
            <div className="text-xs text-yellow-600">Editing {displayName}&apos;s profile</div>
          </div>
        </div>
      )}

      {/* --- Profile Header (hero card) --- */}
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">
          {isAdminMode ? 'Admin View' : 'Player Profile'}
        </div>
        <div className="flex items-center gap-4">
          <div className="relative inline-block shrink-0">
            <UserAvatar user={currentUser} size={80} />
            {isOwnProfile && (
              <button
                onClick={() => setEditingAvatar(true)}
                className="absolute bottom-0 right-0 w-7 h-7 bg-[#006855] rounded-full flex items-center justify-center text-white border-2 border-white shadow-md hover:opacity-90 transition-opacity"
                aria-label="Change profile photo"
              >
                <Camera size={14} />
              </button>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight truncate">{displayName}</h1>
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#dcfce7] text-[#166534] uppercase tracking-wide">
              {role}
            </span>
          </div>
        </div>
      </div>

      {/* --- Avatar Edit Card --- */}
      {isOwnProfile && editingAvatar && (
        <Card className="border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#2c2f30]">Profile Photo</h2>
            <button
              onClick={handleCancelAvatar}
              className="text-[#6b7280] hover:text-[#2c2f30] transition-colors"
              aria-label="Cancel avatar edit"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4">
            {cameraStream ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="rounded-full object-cover"
                    style={{ width: 200, height: 200, backgroundColor: '#000' }}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={capturePhoto}
                    className="w-14 h-14 rounded-full border-4 border-[#006855] bg-white flex items-center justify-center hover:bg-[#006855]/10 transition-colors"
                    aria-label="Capture photo"
                  >
                    <Circle size={28} className="text-[#006855] fill-[#006855]" />
                  </button>
                  <Button variant="ghost" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : avatarPreview ? (
              <div className="flex flex-col items-center gap-4">
                <img
                  src={avatarPreview}
                  alt="Avatar preview"
                  className="rounded-full object-cover"
                  style={{ width: 120, height: 120 }}
                />
                <div className="flex gap-2 w-full">
                  <Button variant="ghost" onClick={() => setAvatarPreview(null)} className="flex-1">
                    Retake
                  </Button>
                  <Button onClick={handleSaveAvatar} disabled={avatarSaving} className="flex-1">
                    {avatarSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <div
                    className="rounded-full bg-[#f5f7f8] flex items-center justify-center text-[#6b7280]"
                    style={{ width: 120, height: 120 }}
                  >
                    <Camera size={40} />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={startCamera}
                    className="flex-1"
                  >
                    Take Selfie
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => uploadInputRef.current?.click()}
                    className="flex-1"
                  >
                    Upload Image
                  </Button>
                </div>

                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                  onChange={handleAvatarInputChange}
                />

                <p className="text-xs text-center text-[#6b7280]">
                  Camera requires HTTPS or localhost. If it doesn't work, use Upload Image.
                </p>

                <div className="flex gap-2 pt-2">
                  <Button variant="ghost" onClick={handleCancelAvatar} className="flex-1" disabled={avatarSaving}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* --- Info Card --- */}
      <Card className="border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#2c2f30]">Contact Info</h2>
          {canEditProfile && !editingContact && (
            <button
              onClick={startEditingContact}
              className="text-xs font-medium text-[#006855] hover:text-[#006855]/80 transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {editingContact ? (
          <div className="space-y-3">
            <Input
              label="Email"
              name="email"
              value={currentUser.email || ''}
              disabled
              className="opacity-60"
            />
            <Input
              label="Telegram Username"
              name="telegram_username"
              value={contactDraft.telegram_username ?? ''}
              autoComplete="username"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setContactDraft((d) => ({ ...d, telegram_username: e.target.value }))
              }
            />
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={cancelEditingContact} className="flex-1" disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSaveContact} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-0">
            {infoFields.map((field, index) => (
              <div
                key={field.label}
                className={`flex justify-between items-center ${
                  index !== infoFields.length - 1 ? 'border-b border-gray-100 pb-3 mb-3' : ''
                }`}
              >
                <span className="text-sm text-[#6b7280]">{field.label}</span>
                <span className="text-sm font-medium text-[#2c2f30] truncate">{field.value}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* --- Payment Methods --- */}
      <Card className="border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#2c2f30]">Payment Methods</h2>
          {canEditProfile && !editingPayment && (
            <button
              onClick={startEditingPayment}
              className="text-xs font-medium text-[#006855] hover:text-[#006855]/80 transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {editingPayment ? (
          <div className="space-y-3">
            <Input
              label="Beem ID"
              name="beem_id"
              value={paymentDraft.beem_id ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPaymentDraft((d) => ({ ...d, beem_id: e.target.value }))
              }
            />
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs font-medium text-[#6b7280] mb-2">Bank Transfer (EFT)</div>
              <div className="space-y-3">
                <Input
                  label="Account Name"
                  name="eft_account_name"
                  value={paymentDraft.eft_account_name ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPaymentDraft((d) => ({ ...d, eft_account_name: e.target.value }))
                  }
                />
                <Input
                  label="BSB"
                  name="eft_bsb"
                  value={paymentDraft.eft_bsb ?? ''}
                  placeholder="000-000"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPaymentDraft((d) => ({ ...d, eft_bsb: formatBsb(e.target.value) }))
                  }
                />
                <Input
                  label="Account Number"
                  name="eft_account_number"
                  value={paymentDraft.eft_account_number ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPaymentDraft((d) => ({ ...d, eft_account_number: formatAccountNumber(e.target.value) }))
                  }
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={cancelEditingPayment} className="flex-1" disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSavePayment} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#6b7280]">Beem</span>
              <span className="text-sm font-medium text-[#2c2f30]">{currentUser.beem_id || '—'}</span>
            </div>
            {hasEft ? (
              <div className="bg-[#f5f7f8] rounded-xl p-4 space-y-3">
                <div className="text-xs font-medium text-[#6b7280] mb-1">Bank Transfer</div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#6b7280]">Account Name</span>
                  <span className="text-sm font-medium text-[#2c2f30]">{currentUser.eft_account_name || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#6b7280]">BSB</span>
                  <span className="text-sm font-mono text-[#2c2f30]">{currentUser.eft_bsb || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#6b7280]">Account Number</span>
                  <span className="text-sm font-mono text-[#2c2f30]">{currentUser.eft_account_number || '—'}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-3 bg-[#f5f7f8] rounded-xl">
                <div className="text-sm font-medium text-[#6b7280]">No bank details</div>
                <div className="text-xs text-[#6b7280]/70 mt-1">Add EFT details to receive bank transfers</div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* --- Preferences --- */}
      {isOwnProfile && (
        <Card className="border border-gray-200">
          <h2 className="text-sm font-semibold text-[#2c2f30] mb-4">Preferences</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1.5">Default Buy-in Method</label>
              <div className="flex bg-[#f5f7f8] rounded-xl p-1 gap-1">
                <button
                  onClick={() => handleUpdatePaymentPreference('digital')}
                  disabled={saving}
                  className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg text-sm font-semibold transition-all ${
                    currentUser.default_buyin_method === 'digital'
                      ? 'bg-white text-[#006855] shadow-sm'
                      : 'bg-transparent text-[#6b7280]'
                  }`}
                >
                  <span>📱</span> Digital
                </button>
                <button
                  onClick={() => handleUpdatePaymentPreference('cash')}
                  disabled={saving}
                  className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg text-sm font-semibold transition-all ${
                    currentUser.default_buyin_method === 'cash'
                      ? 'bg-white text-[#006855] shadow-sm'
                      : 'bg-transparent text-[#6b7280]'
                  }`}
                >
                  <span>💵</span> Cash
                </button>
              </div>
            </div>
            {currentUser.beem_id && hasEft && (
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">Default Payout Method</label>
                <div className="flex bg-[#f5f7f8] rounded-xl p-1 gap-1">
                  <button
                    onClick={() => handleUpdateDefaultPaymentMethod('beem')}
                    disabled={saving}
                    className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg text-sm font-semibold transition-all ${
                      currentUser.default_payment_method === 'beem'
                        ? 'bg-white text-[#006855] shadow-sm'
                        : 'bg-transparent text-[#6b7280]'
                    }`}
                  >
                    Beem
                  </button>
                  <button
                    onClick={() => handleUpdateDefaultPaymentMethod('eft')}
                    disabled={saving}
                    className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg text-sm font-semibold transition-all ${
                      currentUser.default_payment_method === 'eft'
                        ? 'bg-white text-[#006855] shadow-sm'
                        : 'bg-transparent text-[#6b7280]'
                    }`}
                  >
                    EFT
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* --- Password Section --- */}
      {isOwnProfile && currentUser.password_login_enabled && (
        <div className="bg-white border rounded-xl p-4">
          {changingPassword ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#2c2f30]">Change Password</p>
                <button
                  onClick={cancelChangingPassword}
                  className="text-[#6b7280] hover:text-[#2c2f30] transition-colors"
                  aria-label="Cancel password change"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="relative">
                <label htmlFor="profile-current-password" className="block text-xs font-medium text-[#6b7280] mb-1">
                  Current Password
                </label>
                <input
                  id="profile-current-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordDraft.current}
                  autoComplete="current-password"
                  onChange={(e) => setPasswordDraft((d) => ({ ...d, current: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 pr-10 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-[calc(50%+10px)] -translate-y-1/2 text-[#6b7280] hover:text-[#2c2f30]"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <div>
                <label htmlFor="profile-new-password" className="block text-xs font-medium text-[#6b7280] mb-1">
                  New Password
                </label>
                <input
                  id="profile-new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordDraft.next}
                  autoComplete="new-password"
                  onChange={(e) => setPasswordDraft((d) => ({ ...d, next: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
              </div>
              <div>
                <label htmlFor="profile-confirm-password" className="block text-xs font-medium text-[#6b7280] mb-1">
                  Confirm New Password
                </label>
                <input
                  id="profile-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordDraft.confirm}
                  autoComplete="new-password"
                  onChange={(e) => setPasswordDraft((d) => ({ ...d, confirm: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="ghost" onClick={cancelChangingPassword} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handlePasswordChange} disabled={passwordSaving} className="flex-1">
                  {passwordSaving ? 'Saving...' : 'Save Password'}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" onClick={startChangingPassword} className="w-full">
              Change Password
            </Button>
          )}
        </div>
      )}

      {/* --- Linked Providers --- */}
      {isOwnProfile && (
        <Card className="border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[10px] font-black text-[#6b7280] uppercase tracking-[2px]">Linked Providers</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#2c2f30]">Google</span>
              {currentUser.provider_ids?.includes('google.com') ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlink('google.com')}
                  disabled={!canUnlink('google.com')}
                >
                  Unlink
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={handleLinkGoogle}>
                  Link Google
                </Button>
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#2c2f30]">Password</span>
              {currentUser.provider_ids?.includes('password') ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlink('password')}
                  disabled={!canUnlink('password')}
                >
                  Unlink
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => setShowLinkPassword(true)}>
                  Set Password
                </Button>
              )}
            </div>
          </div>

          {showLinkPassword && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#2c2f30]">Set Password Login</p>
                <button
                  onClick={() => {
                    setShowLinkPassword(false);
                    setLinkPasswordDraft({ email: '', password: '' });
                  }}
                  className="text-[#6b7280] hover:text-[#2c2f30] transition-colors"
                  aria-label="Cancel set password"
                >
                  <X size={16} />
                </button>
              </div>
              <div>
                <label htmlFor="link-password-email" className="block text-xs font-medium text-[#6b7280] mb-1">
                  Email
                </label>
                <input
                  id="link-password-email"
                  type="email"
                  value={linkPasswordDraft.email}
                  autoComplete="email"
                  placeholder={currentUser.email ?? ''}
                  onChange={(e) => setLinkPasswordDraft((d) => ({ ...d, email: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
              </div>
              <div>
                <label htmlFor="link-password-password" className="block text-xs font-medium text-[#6b7280] mb-1">
                  Password
                </label>
                <input
                  id="link-password-password"
                  type="password"
                  value={linkPasswordDraft.password}
                  autoComplete="new-password"
                  onChange={(e) => setLinkPasswordDraft((d) => ({ ...d, password: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowLinkPassword(false);
                    setLinkPasswordDraft({ email: '', password: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={handleLinkPassword} disabled={linkPasswordSaving} className="flex-1">
                  {linkPasswordSaving ? 'Saving...' : 'Set Password'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* --- Admin Controls --- */}
      {isAdminMode && (
        <Card className="border-red-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[10px] font-black text-red-600 uppercase tracking-[2px]">Admin Controls</h2>
            {!editingAdmin && (
              <button
                onClick={startEditingAdmin}
                className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                Edit
              </button>
            )}
          </div>

          {editingAdmin ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="admin-account-status" className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Account Status
                </label>
                <select
                  id="admin-account-status"
                  value={adminDraft.account_status}
                  onChange={(e) =>
                    setAdminDraft((d) => ({ ...d, account_status: e.target.value as AdminDraft['account_status'] }))
                  }
                  className="w-full rounded-lg border border-gray-200 bg-white p-3 text-sm font-medium text-[#2c2f30] focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="disabled">Disabled</option>
                  <option value="conflict_review">Conflict Review</option>
                </select>
              </div>

              <div className="space-y-3">
                <div className="text-xs font-medium text-[#6b7280]">Roles</div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminDraft.roles.player === true}
                    onChange={(e) =>
                      setAdminDraft((d) => ({ ...d, roles: { ...d.roles, player: e.target.checked } }))
                    }
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#2c2f30]">Player</div>
                    <div className="text-xs text-[#6b7280]">Can join games, buy chips, participate in entertainment</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminDraft.roles.app_admin === true}
                    disabled={authUser?.id === currentUser.id}
                    onChange={(e) =>
                      setAdminDraft((d) => ({ ...d, roles: { ...d.roles, app_admin: e.target.checked } }))
                    }
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#2c2f30]">Admin</div>
                    <div className="text-xs text-[#6b7280]">Can manage users, edit profiles, access admin pages</div>
                    {authUser?.id === currentUser.id && (
                      <div className="text-xs text-red-500 mt-0.5">You cannot remove your own admin role</div>
                    )}
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminDraft.roles.entertainment_admin === true}
                    onChange={(e) =>
                      setAdminDraft((d) => ({ ...d, roles: { ...d.roles, entertainment_admin: e.target.checked } }))
                    }
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#2c2f30]">Entertainment Admin</div>
                    <div className="text-xs text-[#6b7280]">Can approve expenses, manage entertainment categories</div>
                  </div>
                </label>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={adminDraft.password_login_enabled === true}
                  onChange={(e) =>
                    setAdminDraft((d) => ({ ...d, password_login_enabled: e.target.checked }))
                  }
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-semibold text-[#2c2f30]">Password Login</div>
                  <div className="text-xs text-[#6b7280]">Allow this user to sign in with email and password</div>
                </div>
              </label>

              <div className="flex gap-2 pt-2">
                <Button variant="ghost" onClick={cancelEditingAdmin} className="flex-1" disabled={adminSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveAdmin} disabled={adminSaving} className="flex-1">
                  {adminSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-0">
              <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-3">
                <span className="text-sm text-[#6b7280]">Account Status</span>
                <span className="text-sm font-medium text-[#2c2f30] capitalize">{currentUser.account_status ?? 'active'}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-3">
                <span className="text-sm text-[#6b7280]">Roles</span>
                <span className="text-sm font-medium text-[#2c2f30]">
                  {[
                    currentUser.roles?.player ? 'Player' : null,
                    currentUser.roles?.app_admin ? 'Admin' : null,
                    currentUser.roles?.entertainment_admin ? 'Ent. Admin' : null,
                  ].filter(Boolean).join(', ') || 'None'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6b7280]">Password Login</span>
                <span className="text-sm font-medium text-[#2c2f30]">{currentUser.password_login_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* --- Member Since / Last Active --- */}
      <div className="text-center space-y-1 pt-2">
        {memberSinceFields.map((field) => (
          <div key={field.label} className="text-xs text-[#6b7280]/70">
            {field.label}: {field.value}
          </div>
        ))}
      </div>

      {/* --- Error display --- */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded" role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
}
